import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
} from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useRunDetailsTo} from '../../../components/RunDetails/hooks';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {RenameRunDialog} from '../../../pages/Run/RenameRunDialog';
import {
  RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import {Ref as CustomRunNamesRef} from '../../../state/views/customRunNames/types';
import {
  useRampFlagPinnedRuns,
  useStatsigGateWorkspaceConsolidation,
} from '../../../util/rampFeatureFlags';
import {WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useRunSelectorProjectInfo} from '../RunSelectorRunSetContext';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';
import {RunCopyIdentifiersSubmenu} from './RunCopyIdentifiersSubmenu';
import {RunPinningAndBaselineMenuOptions} from './RunPinningAndBaselineMenuOptions';

type RunRowHeaderMenuProps = {
  row: WBTableRowWithRunSetInfo;
  customRunNamesRef: CustomRunNamesRef | undefined;
  onBenchmarkRun: () => void;
  onCopyRunName: () => void;
  onCopyRunPath: () => void;
  onDeleteRun: () => void;
  onMoveRun: () => void;
  onStopRun: () => void;
  canBenchmarkRun?: boolean;
  canStopRun?: boolean;
};

export function RunRowHeaderMenu(props: RunRowHeaderMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const {entityName} = useRunSelectorProjectInfo();
  const isPinningRunsEnabled = useRampFlagPinnedRuns(entityName);
  const history = useHistory();
  const trackRunMenuWithLocation = (action: RunMenuAction) => {
    trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
  };
  const runUrl = useRunDetailsTo(props.row.name);

  return (
    <>
      <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <RowHeaderMenuTrigger open={menuOpen} setMenuOpen={setMenuOpen} />
        <DropdownPortal>
          <DropdownContent
            align="start"
            sideOffset={8}
            alignOffset={-8}
            style={{zIndex: 1}}>
            {isWorkspaceConsolidationEnabled && (
              <RowHeaderMenuOption
                icon={<Icon name="code-alt" />}
                label="View run logs"
                onSelect={() => {
                  history.push(runUrl('logs'));
                  trackRunMenuWithLocation('View run details');
                }}
              />
            )}
            {isPinningRunsEnabled && (
              <RunPinningAndBaselineMenuOptions row={props.row} />
            )}
            {(isWorkspaceConsolidationEnabled || isPinningRunsEnabled) && (
              <DropdownSeparator />
            )}
            <RowHeaderMenuOption
              icon={<Icon name="pencil-edit" />}
              label="Edit run name"
              onSelect={() => {
                setIsRenameDialogOpen(true);
                trackRunMenuWithLocation('Edit run name');
              }}
            />
            <RunCopyIdentifiersSubmenu
              onCopyRunName={() => {
                props.onCopyRunName();
                trackRunMenuWithLocation('Copy run name');
              }}
              onCopyRunPath={() => {
                props.onCopyRunPath();
                trackRunMenuWithLocation('Copy run path');
              }}
            />
            <DropdownSeparator />

            <RowHeaderMenuOption
              icon={<Icon name="forward-next" />}
              label="Move to another group"
              onSelect={() => {
                props.onMoveRun();
                trackRunMenuWithLocation('Move run groups');
              }}
            />
            <RowHeaderMenuOption
              icon={<Icon name="delete" />}
              label="Delete run"
              onSelect={() => {
                props.onDeleteRun();
                trackRunMenuWithLocation('Delete run');
              }}
            />
            {props.canBenchmarkRun ? (
              <RowHeaderMenuOption
                icon={<Icon name="benchmark-square" />}
                label="Submit to benchmark"
                onSelect={props.onBenchmarkRun}
              />
            ) : null}
            {props.canStopRun ? (
              <RowHeaderMenuOption
                icon={<Icon name="stop" />}
                label="Stop run"
                onSelect={() => {
                  props.onStopRun();
                  trackRunMenuWithLocation('Stop run');
                }}
              />
            ) : null}
          </DropdownContent>
        </DropdownPortal>
      </DropdownRoot>
      {isRenameDialogOpen && (
        <RenameRunDialog
          runId={props.row.id}
          runName={props.row.name}
          runDisplayName={props.row.displayName}
          customRunNamesRef={props.customRunNamesRef}
          closeDialog={() => setIsRenameDialogOpen(false)}
        />
      )}
    </>
  );
}
